import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  getMarcas,
  crearMarca,
  actualizarMarca,
} from "../../../services/usuarios_tienda/marcas";

import BrandFormModal from "./BrandFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./BrandsPage.css";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const VISIBLE_PAGE_COUNT = 3;

const BRAND_STATUS_FILTER_OPTIONS = [
  { key: "ACTIVO", label: "Activos", tone: "activo" },
  { key: "INACTIVO", label: "Inactivos", tone: "inactivo" },
  { key: "ALL", label: "Todos", tone: "all" },
];

function matchesStatusFilter(item, filter) {
  if (filter === "ALL") return true;
  if (filter === "ACTIVO") return Number(item.estado) === 1;
  if (filter === "INACTIVO") return Number(item.estado) !== 1;
  return true;
}

function BrandsPage() {
  const [marcas, setMarcas] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ACTIVO");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [showModal, setShowModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const cargarMarcas = useCallback(async () => {
    try {
      const data = await getMarcas();
      setMarcas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando marcas:", error);
      showAlert("error", error?.message || "No se pudieron cargar las marcas.");
      setMarcas([]);
    }
  }, [showAlert]);

  useEffect(() => {
    cargarMarcas();
  }, [cargarMarcas]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return marcas.filter((m) => {
      if (!matchesStatusFilter(m, statusFilter)) return false;
      if (!q) return true;
      return String(m?.nombre || "").toLowerCase().includes(q);
    });
  }, [marcas, search, statusFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / pageSize)),
    [filtered.length, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  const visiblePages = useMemo(() => {
    const total = totalPages;
    if (total <= VISIBLE_PAGE_COUNT) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, page - Math.floor(VISIBLE_PAGE_COUNT / 2));
    let endPage = startPage + VISIBLE_PAGE_COUNT - 1;
    if (endPage > total) {
      endPage = total;
      startPage = endPage - VISIBLE_PAGE_COUNT + 1;
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }, [page, totalPages]);

  const goToPage = (pageNumber) => {
    if (pageNumber === page || pageNumber < 1 || pageNumber > totalPages) return;
    setPage(pageNumber);
  };

  const nextPage = () => hasNext && setPage(page + 1);
  const prevPage = () => hasPrev && setPage(page - 1);

  const openCreateModal = () => {
    setSelectedBrand(null);
    setShowModal(true);
  };

  const openEditModal = (brand) => {
    setSelectedBrand(brand);
    setShowModal(true);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedBrand) {
        const res = await actualizarMarca(
          selectedBrand.marca_producto_id,
          formData
        );
        showAlert("success", res?.message || "Marca actualizada correctamente.");
      } else {
        const res = await crearMarca(formData);
        showAlert("success", res?.message || "Marca creada correctamente.");
      }

      await cargarMarcas();
      setShowModal(false);
    } catch (error) {
      console.error("Error guardando marca:", error);
      throw error;
    }
  };

  return (
    <div className="tienda-page-scope">
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button
            type="button"
            className="omx-alert__x"
            onClick={() => setAlert(null)}
            aria-label="Cerrar alerta"
          >
            ×
          </button>
        </div>
      )}

      <div className="header">
        <div className="brands-header__row">
          <h2 className="title">Gestión de marcas</h2>

          {hasPerm("create_brand") ? (
            <button type="button" className="brands-btn-create" onClick={openCreateModal}>
              Crear marca
            </button>
          ) : null}
        </div>

        <p className="page-lead">
          Administra las marcas de tu catálogo. Búscalas por nombre y filtra por su estado.
        </p>

        <BrandStatusFilterBar value={statusFilter} onChange={setStatusFilter} />

        <div className="brands-filters">
          <div className="brands-filters__group brands-filters__group--search">
            <label className="brands-filters__label" htmlFor="brands-search">
              Buscar
            </label>
            <div className="brands-filters__search-wrap">
              <input
                id="brands-search"
                type="text"
                placeholder="Nombre de la marca…"
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search.trim() ? (
                <button
                  type="button"
                  className="brands-filters__clear"
                  onClick={() => setSearch("")}
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-card-scroll">
          <table className="modern-table brands-table tienda-table--cards">
            <thead>
              <tr>
                <th className="brands-col-id">ID</th>
                <th>Nombre de marca</th>
                <th>Estado</th>
                <th className="brands-col-fecha">Fecha de creación</th>
                <th className="brands-col-actions" style={{ textAlign: "right" }}>
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={5} className="no-data">
                    No se encontraron marcas
                  </td>
                </tr>
              ) : (
                paginated.map((b) => (
                  <tr key={b.marca_producto_id} className="brands-row">
                    <td className="brands-col-id" data-label="ID">
                      {b.marca_producto_id}
                    </td>
                    <td data-label="Nombre de marca">
                      <span className="brands-name">{b.nombre}</span>
                    </td>
                    <td data-label="Estado">
                      <span
                        className={
                          Number(b.estado) === 1 ? "status active" : "status inactive"
                        }
                      >
                        {Number(b.estado) === 1 ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="brands-col-fecha" data-label="Fecha de creación">
                      {b.fecha_creado?.slice(0, 10) || "—"}
                    </td>
                    <td
                      className="brands-col-actions"
                      data-label="Acciones"
                      style={{ textAlign: "right" }}
                    >
                      {hasPerm("update_brand") && (
                        <span
                          className="action-icon edit"
                          onClick={() => openEditModal(b)}
                          title="Editar marca"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && openEditModal(b)}
                        >
                          {IconEdit}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="pagination">
            <div className="pagination__center">
              <div className="pagination__nav" aria-label="Paginación de marcas">
                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={prevPage}
                  disabled={!hasPrev}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <div className="pagination__pages">
                  {visiblePages.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`pagination__page${
                        pageNumber === page ? " is-active" : ""
                      }`}
                      onClick={() => goToPage(pageNumber)}
                      aria-label={`Ir a página ${pageNumber}`}
                      aria-current={pageNumber === page ? "page" : undefined}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={nextPage}
                  disabled={!hasNext}
                  aria-label="Página siguiente"
                >
                  ›
                </button>
              </div>

              <div className="pagination__size">
                <span className="pagination__size-label">Mostrar</span>
                <select
                  className="page-size-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  title="Filas por página"
                  aria-label="Filas por página"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="pagination__size-label">por página</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <BrandFormModal
          isOpen={showModal}
          brandData={selectedBrand}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

function BrandStatusFilterBar({ value, onChange }) {
  return (
    <div className="brands-status-filter" role="group" aria-label="Filtrar por estado">
      <span className="brands-status-filter__label">Ver</span>
      <div className="brands-status-filter__options">
        {BRAND_STATUS_FILTER_OPTIONS.map((opt) => {
          const toneClass = opt.tone && opt.tone !== "all" ? ` is-${opt.tone}` : "";
          return (
            <button
              key={opt.key}
              type="button"
              className={`brands-status-filter__btn${
                value === opt.key ? " is-active" : ""
              }${toneClass}`}
              aria-pressed={value === opt.key}
              onClick={() => onChange(opt.key)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default BrandsPage;
